import {
  ArrowsClockwiseIcon,
  CloudIcon,
  FolderOpenIcon,
  SpinnerGapIcon,
  UploadSimpleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useRef, useState } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { buildArchive, mergeArchive, summariseMerge } from '@/features/backup';
import { settings } from '@/lib/db';
import { formatLocalDate, formatLocalTime } from '@/lib/format';
import { defaultDeviceName } from '@/lib/platform';
import { shareOrSaveFile } from '@/lib/track-export';

import { syncNow, type TargetState, useSyncState } from './controller';
import { pickFolder, supportsFolderSync, type SyncFolderFile } from './folder';
import { disconnectICloud, icloudAvailable, startICloudSignIn } from './icloud';
import {
  deviceFileName,
  isOwnFile,
  resetTarget,
  storeFolder,
  SYNC_KEYS,
  syncIdentity,
} from './sync';

/**
 * Settings → Sync folder. See `sync.ts` for how it works and `docs/SYNC.md`
 * for why.
 *
 * Two modes, chosen by what the browser can do rather than by a switch:
 * automatic where a folder can be kept open (Chromium on a desktop, the
 * desktop app), and by hand everywhere else — which in practice means the
 * iPhone, where the pilot merges the other devices' files from the Files
 * picker and saves this one's back through the share sheet.
 */

export const SYNC_HASH = 'sync';

function formatInstant(iso: string): string {
  return `${formatLocalDate(iso)}, ${formatLocalTime(iso)}`;
}

export function SyncPanel() {
  const folderOnDisk = supportsFolderSync();
  const icloud = icloudAvailable();
  return (
    <section
      id={SYNC_HASH}
      className="border-border bg-surface rounded-container scroll-mt-4 border p-4"
    >
      <h2 className="text-sm font-semibold tracking-tight">Sync</h2>
      <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
        Keeps a copy of all data outside this device and shares it between devices.
      </p>
      {icloud ? <ICloudSync /> : null}
      <div className="border-border mt-4 border-t pt-4">
        <h3 className="text-sm font-medium">Folder</h3>
        <p className="text-muted-foreground mt-0.5 text-xs">
          A folder in iCloud Drive, OneDrive or Dropbox.
        </p>
        {folderOnDisk ? <FolderSync /> : <ManualSync />}
      </div>
      <DeviceSettings />
    </section>
  );
}

function statusText(state: TargetState, expired: string): string {
  switch (state.phase) {
    case 'syncing':
      return 'Syncing';
    case 'needs-permission':
      return expired;
    case 'error':
      return 'Sync failed';
    default:
      return state.lastSyncAt === undefined ? '—' : `Synced ${formatInstant(state.lastSyncAt)}`;
  }
}

function TargetDetails({ state, label }: { state: TargetState; label: string }) {
  return (
    <>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">{label}</dt>
        <dd className="min-w-0 truncate" title={state.label}>
          {state.label ?? '—'}
        </dd>
        <dt className="text-muted-foreground">Status</dt>
        <dd>
          {statusText(state, label === 'Account' ? 'Sign-in required' : 'Folder access required')}
        </dd>
      </dl>
      {state.phase === 'error' && state.message !== undefined ? (
        <ErrorLine message={state.message} />
      ) : null}
      {state.lastResult !== undefined && state.lastResult.merged.length > 0 ? (
        <p className="text-muted-foreground text-xs">
          {state.lastResult.merged
            .map(({ summary }) => summariseMerge(summary))
            .filter((line) => line !== 'No changes.')
            .join(' ') || 'No changes.'}
        </p>
      ) : null}
    </>
  );
}

function SpinOrSync({ busy }: { busy: boolean }) {
  return busy ? (
    <SpinnerGapIcon
      aria-hidden="true"
      weight={ICON_WEIGHT}
      className="size-4 motion-safe:animate-spin"
    />
  ) : (
    <ArrowsClockwiseIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
  );
}

/** iCloud through CloudKit. See `icloud.ts`. */
function ICloudSync() {
  const state = useSyncState((all) => all.icloud);
  const [error, setError] = useState<string | null>(null);
  const busy = state.phase === 'syncing';

  const signIn = async () => {
    setError(null);
    try {
      if (await startICloudSignIn()) {
        await resetTarget('icloud');
        await syncNow({ only: 'icloud' });
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'iCloud sign-in failed.');
    }
  };

  return (
    <div className="border-border mt-4 space-y-3 border-t pt-4">
      <h3 className="text-sm font-medium">iCloud</h3>
      {state.phase === 'off' ? null : <TargetDetails state={state} label="Account" />}
      {error === null ? null : <ErrorLine message={error} />}
      <div className="flex flex-wrap gap-2">
        {state.phase === 'off' || state.phase === 'needs-permission' ? (
          <Button
            onClick={() => {
              void signIn();
            }}
          >
            <CloudIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
            Sign in to iCloud
          </Button>
        ) : (
          <Button
            disabled={busy}
            onClick={() => {
              void syncNow({ interactive: true, only: 'icloud' });
            }}
          >
            <SpinOrSync busy={busy} />
            Sync now
          </Button>
        )}
        {state.phase === 'off' ? null : (
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              void disconnectICloud().then(() => syncNow({ only: 'icloud' }));
            }}
          >
            Sign out
          </Button>
        )}
      </div>
      {state.phase === 'off' || state.phase === 'needs-permission' ? (
        <p className="text-muted-foreground text-xs">
          Select “Keep me signed in” to stay signed in for two weeks.
        </p>
      ) : null}
      <FolderFiles files={state.files} title="Files in iCloud" />
    </div>
  );
}

/** A folder on disk: the desktop app, or Chromium on a desktop. */
function FolderSync() {
  const state = useSyncState((all) => all.folder);
  const [error, setError] = useState<string | null>(null);

  const choose = async () => {
    setError(null);
    try {
      const folder = await pickFolder();
      if (folder === null) {
        return;
      }
      await storeFolder(folder);
      await syncNow({ interactive: true, only: 'folder' });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Folder could not be opened.');
    }
  };

  const stop = async () => {
    await storeFolder(null);
    await syncNow({ only: 'folder' });
  };

  if (state.phase === 'off') {
    return (
      <div className="mt-3 space-y-2">
        <Button
          variant="outline"
          onClick={() => {
            void choose();
          }}
        >
          <FolderOpenIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
          Choose folder
        </Button>
        {error === null ? null : <ErrorLine message={error} />}
      </div>
    );
  }

  const busy = state.phase === 'syncing';
  return (
    <div className="mt-3 space-y-3">
      <TargetDetails state={state} label="Folder" />
      {error === null ? null : <ErrorLine message={error} />}
      <div className="flex flex-wrap gap-2">
        {state.phase === 'needs-permission' ? (
          <Button
            onClick={() => {
              void syncNow({ interactive: true, only: 'folder' });
            }}
          >
            Allow access
          </Button>
        ) : (
          <Button
            disabled={busy}
            onClick={() => {
              void syncNow({ interactive: true, only: 'folder' });
            }}
          >
            <SpinOrSync busy={busy} />
            Sync now
          </Button>
        )}
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            void choose();
          }}
        >
          Change folder
        </Button>
        <Button
          variant="ghost"
          disabled={busy}
          onClick={() => {
            void stop();
          }}
        >
          Stop syncing
        </Button>
      </div>
      <FolderFiles files={state.files} title="Files in folder" />
    </div>
  );
}

/** Each device's file, so a stale one from a retired device is visible. */
function FolderFiles({ files, title }: { files: readonly SyncFolderFile[]; title: string }) {
  const identity = useLiveQuery(() => syncIdentity(), []);
  if (files.length === 0 || identity === undefined) {
    return null;
  }
  return (
    <div className="border-border border-t pt-3">
      <h4 className="text-xs font-medium">{title}</h4>
      <ul className="mt-1 space-y-0.5 text-xs">
        {[...files]
          .sort((a, b) => b.lastModified - a.lastModified)
          .map((file) => (
            <li key={file.name} className="flex justify-between gap-3">
              <span className="min-w-0 truncate">
                {file.name}
                {isOwnFile(file.name, identity) ? (
                  <span className="text-muted-foreground"> (this device)</span>
                ) : null}
              </span>
              <span className="text-muted-foreground tabular shrink-0">
                {file.lastModified > 0
                  ? formatInstant(new Date(file.lastModified).toISOString())
                  : ''}
              </span>
            </li>
          ))}
      </ul>
    </div>
  );
}

/**
 * Sync by hand, for a browser that cannot keep a folder open. The same files,
 * the same merge: this only replaces the trigger.
 */
function ManualSync() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ kind: 'ok' | 'error'; message: string } | null>(null);

  const merge = async (files: readonly File[]) => {
    setBusy(true);
    setResult(null);
    const identity = await syncIdentity();
    const lines: string[] = [];
    try {
      for (const file of files) {
        if (isOwnFile(file.name, identity)) {
          continue;
        }
        const summary = await mergeArchive(new Uint8Array(await file.arrayBuffer()), {
          mode: 'sync',
        });
        lines.push(summariseMerge(summary));
      }
      const changed = lines.filter((line) => line !== 'No changes.');
      setResult({ kind: 'ok', message: changed.length > 0 ? changed.join(' ') : 'No changes.' });
    } catch (error) {
      setResult({
        kind: 'error',
        message: error instanceof Error ? error.message : 'File could not be merged.',
      });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setResult(null);
    try {
      const identity = await syncIdentity();
      const bytes = await buildArchive({
        includeOriginals: await settings.get(SYNC_KEYS.includeOriginals, false),
        device: identity,
      });
      await shareOrSaveFile(deviceFileName(identity), bytes, 'application/zip');
    } catch (error) {
      setResult({
        kind: 'error',
        message: error instanceof Error ? error.message : 'File could not be created.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 space-y-3">
      <p className="text-muted-foreground text-xs">
        This browser cannot sync a folder automatically. Merge the other devices’ files, then save
        this device’s file to the same folder.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          disabled={busy}
          onClick={() => {
            inputRef.current?.click();
          }}
        >
          <ArrowsClockwiseIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
          Merge sync files
        </Button>
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            void save();
          }}
        >
          <UploadSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
          Save this device’s file
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".fdv"
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
          data-testid="sync-merge-input"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = '';
            if (files.length > 0) {
              void merge(files);
            }
          }}
        />
      </div>
      {result === null ? null : result.kind === 'error' ? (
        <ErrorLine message={result.message} />
      ) : (
        <p className="text-muted-foreground text-xs">{result.message}</p>
      )}
    </div>
  );
}

function DeviceSettings() {
  const name = useLiveQuery(() => settings.get<string | null>(SYNC_KEYS.deviceName, null), []);
  const includeOriginals = useLiveQuery(() => settings.get(SYNC_KEYS.includeOriginals, false), []);

  return (
    <div className="border-border mt-4 grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-2">
      {/* Keyed on the stored name, so the field starts over when it changes elsewhere. */}
      {name === undefined ? <span /> : <DeviceNameField key={name ?? ''} stored={name ?? ''} />}
      <label className="flex items-center gap-2 self-end text-sm">
        <input
          type="checkbox"
          checked={includeOriginals === true}
          onChange={(event) => {
            void settings.set(SYNC_KEYS.includeOriginals, event.target.checked);
          }}
        />
        Include original track files
      </label>
    </div>
  );
}

function DeviceNameField({ stored }: { stored: string }) {
  const [draft, setDraft] = useState(stored);
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-muted-foreground">Device name</span>
      <input
        className="border-border bg-surface rounded-control focus-visible:outline-ring border px-2.5 py-1.5 text-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2"
        value={draft}
        placeholder={defaultDeviceName()}
        maxLength={40}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={() => {
          if (draft.trim() !== stored) {
            void settings.set(SYNC_KEYS.deviceName, draft.trim());
          }
        }}
      />
    </label>
  );
}

function ErrorLine({ message }: { message: string }) {
  return (
    <p role="alert" className="text-danger flex items-center gap-1.5 text-xs">
      <WarningCircleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-3.5 shrink-0" />
      {message}
    </p>
  );
}
